import PDFPage from './PDFPage'
import type { ReportData, BrandingConfig } from '@/types'
import { formatCurrency } from '@/utils/calculations'

interface ValueBreakdownPageProps {
  data: ReportData
  branding: BrandingConfig
  pageNumber: number
  totalPages: number
}

export default function ValueBreakdownPage({
  data,
  branding,
  pageNumber,
  totalPages,
}: ValueBreakdownPageProps) {
  const isLight = branding.theme === 'light'
  const cardBg = isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)'
  const cardBorder = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'

  // Calculate total for percentage calculations
  const totalAttributed = data.attributions.reduce((sum, a) => sum + a.attributedValue, 0)

  return (
    <PDFPage
      pageNumber={pageNumber}
      totalPages={totalPages}
      title="Value Breakdown"
      subtitle="Vanguard Adviser's Alpha"
      branding={branding}
    >
      {/* Introduction */}
      <div style={{ marginBottom: '24px' }}>
        <p style={{ fontSize: '13px', lineHeight: 1.6, opacity: 0.8 }}>
          Research by Vanguard identifies seven key areas where professional financial advice adds
          measurable value. The combined impact, known as &quot;Adviser&apos;s Alpha,&quot;
          represents approximately 3% additional returns per year.
        </p>
      </div>

      {/* Value Attribution List */}
      <div
        style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
        }}
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>
          Where the Value Comes From
        </h3>

        {data.attributions.map((attr, index) => (
          <div
            key={attr.id}
            style={{
              padding: '12px 0',
              borderBottom:
                index < data.attributions.length - 1 ? `1px solid ${cardBorder}` : 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '8px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '3px',
                    backgroundColor: attr.color,
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div style={{ fontWeight: 500, fontSize: '13px' }}>{attr.name}</div>
                  <div style={{ fontSize: '11px', opacity: 0.6 }}>{attr.description}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '14px', color: branding.primaryColor }}>
                  {formatCurrency(attr.attributedValue)}
                </div>
                <div style={{ fontSize: '11px', opacity: 0.6 }}>{attr.valuePercent}% annual</div>
              </div>
            </div>

            {/* Progress bar */}
            <div
              style={{
                height: '6px',
                background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.05)',
                borderRadius: '3px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(attr.attributedValue / totalAttributed) * 100}%`,
                  backgroundColor: attr.color,
                  borderRadius: '3px',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Total Summary */}
      <div
        style={{
          background: `linear-gradient(135deg, ${branding.primaryColor}15 0%, ${branding.secondaryColor}15 100%)`,
          border: `1px solid ${branding.primaryColor}30`,
          borderRadius: '12px',
          padding: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>Total Adviser&apos;s Alpha</div>
          <div style={{ fontWeight: 600, fontSize: '16px', marginTop: '4px' }}>
            ~3% additional returns annually
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', opacity: 0.7 }}>Your {data.years}-Year Benefit</div>
          <div
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: '28px',
              fontWeight: 600,
              color: branding.primaryColor,
            }}
          >
            {formatCurrency(data.results.valueAdded)}
          </div>
        </div>
      </div>

      {/* Behavioural Coaching Highlight */}
      <div
        style={{
          marginTop: '24px',
          padding: '16px',
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: '#ec489920',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>
              Behavioural Coaching: The Biggest Factor
            </div>
            <p style={{ fontSize: '12px', opacity: 0.7, lineHeight: 1.5, margin: 0 }}>
              At ~2% annually, behavioural coaching is the single largest component of adviser value.
              This includes helping clients stay disciplined during market volatility, avoiding
              emotional decisions, and maintaining a long-term perspective.
            </p>
          </div>
        </div>
      </div>
    </PDFPage>
  )
}
